import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export type DashboardRange = '1d' | '7d' | '15d' | '30d' | '60d' | '90d' | 'custom';

export interface DashboardMetricPoint {
  key: string;
  label: string;
  value: number;
}

export interface DashboardSummary {
  openComplaints: number;
  newOpenComplaints: number;
  newComplaintsInRange: number;
  reopenedComplaints: number;
  overdueComplaints: number;
  dueToday: number;
  dueTomorrow: number;
}

export interface DashboardUserRequestMetrics {
  /** Current pending count across the administrator's authorized zones. */
  pendingRequests: number;
  /** Decisions whose request update timestamp falls in the selected range. */
  approvedInRange: number;
  rejectedInRange: number;
  /** Incoming registrations grouped using the dashboard's selected trend granularity. */
  requestTrend: DashboardMetricPoint[];
}

export interface DashboardMetrics {
  range: DashboardRange;
  rangeLabel: string;
  fromDate: string;
  toDate: string;
  trendGranularity: 'hour' | 'day' | 'week' | 'month';
  generatedAt: string;
  summary: DashboardSummary;
  complaintStatuses: DashboardMetricPoint[];
  jobCardStatuses: DashboardMetricPoint[];
  severityDistribution: DashboardMetricPoint[];
  residentPriorityDistribution: DashboardMetricPoint[];
  categoryBreakdown: DashboardMetricPoint[];
  areaBreakdown: DashboardMetricPoint[];
  departmentWorkload: DashboardMetricPoint[];
  /** Point keys identify the department (-1 for unassigned); labels identify the SLA. */
  departmentSeverityBreakdown: DashboardMetricPoint[];
  employeeWorkload: DashboardMetricPoint[];
  complaintTrend: DashboardMetricPoint[];
  /** Present only for administrators; the API omits it for other staff roles. */
  userRequestMetrics?: DashboardUserRequestMetrics | null;
}

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private readonly apiUrl = `${environment.apiUrl}/dashboard/v1/metrics`;

  constructor(private readonly http: HttpClient) {}

  getMetrics(range: DashboardRange, fromDate?: string, toDate?: string): Observable<DashboardMetrics> {
    let params = new HttpParams().set('range', range);
    if (range === 'custom' && fromDate && toDate) {
      params = params.set('fromDate', fromDate).set('toDate', toDate);
    }

    return this.http.get<DashboardMetrics>(this.apiUrl, { params });
  }
}
